import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { confirmar } from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { Texto } from "@/components/IATexto";
import ConsumoIA from "@/pages/admin/InnovaIAConsumo";
import { Send, Loader2, ThumbsUp, ThumbsDown, PlayCircle, FileText, ExternalLink, MessageSquarePlus, X, UserRound, CheckCircle2, ChevronDown, ChevronUp } from "lucide-react";
import { Paginacao, usePaginas } from "@/components/Paginacao";
import { NucleoIcone } from "@/components/InnovaIAAluna";

const EXEMPLOS = [
  "O cabelo da minha cliente emborrachou depois que apliquei a descoloração, como posso inverter essa ação?",
  "Como tratar um cabelo rígido que fez muita progressiva ácida?",
  "Qual a diferença entre cabelo rígido e emborrachado?",
  "O que precisa ter no rótulo do meu shampoo artesanal?",
];

function Turno({ r, onMudou }) {
  const [correcao, setCorrecao] = useState(r.correcao || "");
  const [abrir, setAbrir] = useState(false);
  const [respProf, setRespProf] = useState("");
  const avaliar = async (nota) => {
    try { await api.post(`/admin/innova-ia/${r.id}/avaliar`, { nota, correcao }); toast.success(nota === "bom" ? "Marcada como certa: vira resposta aprovada." : "Correção salva."); onMudou(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const encaminhar = async () => {
    try { await api.post(`/admin/innova-ia/${r.id}/encaminhar`); toast.success("Dúvida encaminhada à professora."); onMudou(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const responder = async () => {
    try { await api.post(`/admin/innova-ia/${r.id}/responder`, { resposta: respProf }); toast.success("Resposta salva. A próxima pergunta igual recebe esta resposta."); onMudou(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        <span className="h-8 w-8 shrink-0 rounded-full grid place-items-center bg-slate-100 text-slate-500"><UserRound className="h-4 w-4" /></span>
        <div className="font-semibold text-slate-800 pt-1">{r.pergunta}</div>
      </div>
      <div className="flex items-start gap-3">
        <img src="/brand/app-192.png" alt="" className="h-8 w-8 shrink-0 rounded-full" />
        <div className="flex-1 min-w-0">
          {r.da_faq && <div className="mb-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#1E9E2A] bg-green-50 border border-green-100 rounded-full px-2 py-0.5"><CheckCircle2 className="h-3 w-3" /> Resposta aprovada reaproveitada · sem custo</div>}
          {r.trava_prova && <div className="mb-2 inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 bg-red-50 border border-red-100 rounded-full px-2 py-0.5">Trava da prova: pergunta parecida com questão de prova · sem custo</div>}
          {r.origem === "professora" && <div className="mb-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#926B38] bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5">Resposta da professora</div>}
          <Texto t={r.resposta} />
          {r.fontes?.length > 0 && (
            <ol className="mt-2 space-y-1 text-sm border-t border-slate-100 pt-2">
              {r.fontes.map((f, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-[#1E9E2A] w-6">[{i + 1}]</span>
                  {f.tipo === "aula" ? <PlayCircle className="h-4 w-4 text-slate-400 shrink-0" /> : <FileText className="h-4 w-4 text-slate-400 shrink-0" />}
                  <span className="text-slate-700 truncate">{f.titulo}{f.curso ? <span className="text-slate-400"> · {f.curso}</span> : ""} · <b>{f.ref}</b></span>
                  {f.link && <a href={f.link} target="_blank" rel="noopener" className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E9E2A] shrink-0" title={f.tipo === "aula" ? "Abrir a aula já neste minuto" : "Abrir"}>
                    {f.tipo === "aula" ? <>▶ assistir no {f.ref}</> : <ExternalLink className="h-3.5 w-3.5" />}</a>}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
      <div className="ml-11 flex flex-wrap items-center gap-2">
        <Button size="sm" variant={r.nota === "bom" ? "default" : "outline"} className={r.nota === "bom" ? "bg-[#1E9E2A] text-white" : ""} onClick={() => avaliar("bom")}><ThumbsUp className="h-4 w-4 mr-1" /> Certa</Button>
        <Button size="sm" variant={r.nota === "ruim" ? "default" : "outline"} className={r.nota === "ruim" ? "bg-red-600 text-white" : ""} onClick={() => setAbrir(true)}><ThumbsDown className="h-4 w-4 mr-1" /> Precisa corrigir</Button>
        {!r.duvida && r.origem !== "professora" && <Button size="sm" variant="outline" onClick={encaminhar}>Encaminhar à professora</Button>}
        <span className="ml-auto text-[11px] text-slate-400">{r.por} · {r.segundos ?? 0}s · US$ {Number(r.custo_usd || 0).toFixed(3)}</span>
        {(abrir || r.correcao) && (
          <div className="w-full space-y-2 pt-1">
            <Textarea value={correcao} onChange={(e) => setCorrecao(e.target.value)} rows={3} placeholder="Como a resposta deveria ser? (vira ajuste da IA)" />
            <Button size="sm" className="bg-red-600 text-white" onClick={() => avaliar("ruim")} disabled={!correcao.trim()}>Salvar correção</Button>
          </div>
        )}
        {r.duvida?.status === "aberta" && (
          <div className="w-full rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
            <div className="text-sm font-semibold text-amber-800">Dúvida com a professora: aguardando resposta</div>
            <Textarea value={respProf} onChange={(e) => setRespProf(e.target.value)} rows={3} placeholder="Resposta da professora (fica aprovada: a próxima aluna que perguntar igual recebe esta)" />
            <Button size="sm" className="text-white" style={{ background: "#926B38" }} onClick={responder} disabled={respProf.trim().length < 3}>Salvar resposta da professora</Button>
          </div>
        )}
        {r.duvida?.status === "respondida" && (
          <div className="w-full rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-slate-700"><b className="text-[#1E9E2A]">Professora respondeu:</b> {r.duvida.resposta}</div>
        )}
      </div>
    </div>
  );
}

/** Alunas testadoras: veem a IA mesmo escondida, como alunas de verdade. Busca pelo nome, e-mail ou CPF. */
function Testadoras({ onMudou }) {
  const [d, setD] = useState({ testadoras: [], achadas: [] });
  const [q, setQ] = useState("");
  const carregar = useCallback((busca = "") => api.get("/admin/innova-ia/testadoras", { params: { q: busca } }).then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { const t = setTimeout(() => carregar(q), 350); return () => clearTimeout(t); }, [q, carregar]);
  const marcar = async (a, ativo) => {
    try { await api.put(`/admin/innova-ia/aluna/${a.id}/testadora`, { ativo }); toast.success(ativo ? `IEP Intelligence ativada para ${a.name}.` : `IEP Intelligence desativada para ${a.name}.`); carregar(q); onMudou?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const ids = new Set(d.testadoras.map((a) => a.id));
  return (
    <div className="space-y-2 border-t border-slate-100 pt-4">
      <div className="text-xs text-slate-500">Alunas com a IA ativada (veem mesmo antes do lançamento, como alunas de verdade: gastam as grátis e podem comprar)</div>
      <div className="flex flex-wrap gap-2">
        {d.testadoras.length === 0 && <span className="text-sm text-slate-400">Nenhuma ainda.</span>}
        {d.testadoras.map((a) => (
          <span key={a.id} className="inline-flex items-center gap-1.5 rounded-full bg-green-50 border border-green-200 pl-3 pr-1 py-1 text-sm text-slate-700">
            {a.name}<button onClick={() => marcar(a, false)} className="rounded-full p-0.5 hover:bg-green-100" aria-label="Desativar" title="Desativar"><X className="h-3.5 w-3.5" /></button>
          </span>))}
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ativar para uma aluna: digite o nome, e-mail ou CPF" className="max-w-md" />
      {d.achadas.filter((a) => !ids.has(a.id)).map((a) => (
        <div key={a.id} className="flex items-center gap-2 max-w-md text-sm">
          <span className="truncate">{a.name} <span className="text-slate-400">· {a.email}</span></span>
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => { marcar(a, true); setQ(""); }}>Ativar</Button>
        </div>))}
    </div>
  );
}

/** Liberação para as alunas, perguntas grátis e pacotes de crédito. */
function ConfigIA() {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/innova-ia/config").then((r) => setC(r.data)).catch(() => {}); }, []);
  if (!c) return null;
  const salvar = async (novo) => {
    setBusy(true);
    try { const r = await api.put("/admin/innova-ia/config", novo); setC((x) => ({ ...x, ...r.data })); toast.success("Configuração salva."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const ligar = async (v) => {
    const ok = await confirmar(v
      ? "Liberar a IEP Intelligence para TODAS as alunas? O botão aparece na área delas na hora."
      : "Esconder a IEP Intelligence das alunas? O botão some da área delas.");
    if (ok) salvar({ ...c, alunas_ativo: v });
  };
  const pac = (i, campo, v) => setC({ ...c, pacotes: c.pacotes.map((p, k) => (k === i ? { ...p, [campo]: v } : p)) });
  return (
    <div className={`bg-white rounded-xl border shadow-sm p-5 space-y-4 ${c.alunas_ativo ? "border-[#1E9E2A]" : "border-slate-200"}`} data-testid="ia-config">
      <div className="flex flex-wrap items-center gap-3">
        <Switch checked={!!c.alunas_ativo} disabled={busy} onCheckedChange={ligar} />
        <div>
          <div className="font-semibold text-slate-800">{c.alunas_ativo ? "Liberada para as alunas" : "Escondida das alunas"}</div>
          <div className="text-xs text-slate-500">{c.alunas_ativo ? "O botão “IEP Intelligence” aparece para as alunas com matrícula ativa nos cursos marcados abaixo." : `Só aparece para as alunas que você ativar (${c.testadoras || 0}). Escolha logo abaixo.`}</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        <Switch checked={!!c.certificadas_ativo} disabled={busy} onCheckedChange={async (v) => {
          if (await confirmar(v ? "Mostrar a IEP Intelligence para TODAS as alunas com certificado (nos cursos marcados) para experimentar?" : "Esconder o “experimentar” das alunas com certificado?")) salvar({ ...c, certificadas_ativo: v });
        }} />
        <div className="text-sm flex-1 min-w-[240px]"><b>Experimentar para alunas com certificado</b>
          <div className="text-xs text-slate-500">Quem tem certificado vê o aviso e o botão, faz {c.experimente_qtd} pergunta(s) e depois é convidada a falar com vocês no WhatsApp (sem pacotes).</div></div>
        <label className="text-xs text-slate-500 flex items-center gap-1">Perguntas <Input type="number" min={1} max={20} className="w-16 h-8" value={c.experimente_qtd} onChange={(e) => setC({ ...c, experimente_qtd: Number(e.target.value) })} /></label>
        <label className="text-xs text-slate-500 flex items-center gap-1">WhatsApp <Input className="w-40 h-8" value={c.whatsapp} onChange={(e) => setC({ ...c, whatsapp: e.target.value.replace(/\D/g, "") })} /></label>
      </div>
      <div className="grid sm:grid-cols-[auto_auto_1fr] gap-4 items-end">
        <label className="space-y-1"><span className="text-xs text-slate-500">Perguntas grátis</span>
          <Input type="number" min={0} className="w-24" value={c.gratis} onChange={(e) => setC({ ...c, gratis: Number(e.target.value) })} /></label>
        <label className="space-y-1"><span className="text-xs text-slate-500">Período</span>
          <select className="h-10 rounded-md border border-slate-200 px-2 text-sm" value={c.periodo} onChange={(e) => setC({ ...c, periodo: e.target.value })}>
            <option value="mes">por mês</option><option value="total">no total (uma vez)</option></select></label>
        <div className="space-y-1"><span className="text-xs text-slate-500">Pacotes (quantidade · preço R$)</span>
          <div className="flex flex-wrap gap-2">{c.pacotes.map((p, i) => (
            <div key={p.id} className="flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1">
              <Input type="number" min={1} className="w-20 h-8" value={p.qtd} onChange={(e) => pac(i, "qtd", Number(e.target.value))} />
              <span className="text-xs text-slate-400">por</span>
              <Input type="number" min={0.01} step="0.01" className="w-24 h-8" value={p.preco} onChange={(e) => pac(i, "preco", Number(e.target.value))} />
            </div>))}</div></div>
      </div>
      <div className="space-y-1"><span className="text-xs text-slate-500">Cursos que incluem a IEP Intelligence (precisa de matrícula ativa; nenhum marcado = qualquer curso)</span>
        <div className="flex flex-wrap gap-x-4 gap-y-1">{(c.cursos_disponiveis || []).map((k) => (
          <label key={k.id} className="inline-flex items-center gap-1.5 text-sm text-slate-700">
            <input type="checkbox" checked={(c.cursos || []).includes(k.id)}
              onChange={(e) => setC({ ...c, cursos: e.target.checked ? [...(c.cursos || []), k.id] : (c.cursos || []).filter((x) => x !== k.id) })} /> {k.title}
          </label>))}</div></div>
      <Button size="sm" disabled={busy} onClick={() => salvar(c)} className="text-white" style={{ background: "#232D4F" }}>Salvar limites, cursos e pacotes</Button>
      <Testadoras onMudou={() => api.get("/admin/innova-ia/config").then((r) => setC((x) => ({ ...x, testadoras: r.data.testadoras }))).catch(() => {})} />
    </div>
  );
}

export default function InnovaIA() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState(null);
  const [conversa, setConversa] = useState("");
  const [abertas, setAbertas] = useState(() => new Set());    // conversas abertas na lista (fechadas mostram só a 1ª pergunta)
  const alternar = (k) => setAbertas((a) => { const n = new Set(a); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const carregar = () => api.get("/admin/innova-ia/historico").then((r) => setHist(r.data)).catch(() => setHist({ itens: [], totais: {} }));
  useEffect(() => { carregar(); }, []);
  const perguntar = async (texto) => {
    const p = (texto ?? q).trim();
    if (p.length < 3) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/innova-ia/perguntar", { pergunta: p, conversa_id: texto ? "" : conversa });
      setConversa(r.data.conversa_id); setQ(""); await carregar();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  // agrupa o histórico em conversas (mais recente em cima; dentro, na ordem em que foi perguntado)
  const conversas = useMemo(() => {
    const g = new Map();
    (hist?.itens || []).forEach((r) => { const k = r.conversa_id || r.id; if (!g.has(k)) g.set(k, []); g.get(k).push(r); });
    return [...g.entries()].map(([k, v]) => [k, v.sort((a, b) => (a.at < b.at ? -1 : 1))]);
  }, [hist]);
  const pag = usePaginas(conversas, 15);
  const t = hist?.totais || {};
  return (
    <div className="space-y-6 animate-fade-up">
      <div className="rounded-2xl p-6 text-white" style={{ background: "linear-gradient(135deg,#1A2340,#232D4F 55%,#1E9E2A)" }}>
        <div className="flex items-center gap-3"><NucleoIcone className="h-8 w-8" /><h1 className="font-display text-3xl font-bold">IEP Intelligence</h1></div>
        <p className="text-slate-200/90 mt-2 max-w-3xl">Painel da IEP Intelligence. Libere para as alunas no quadro abaixo. Aqui você pergunta como uma aluna, continue a conversa, confira as fontes e marque se a resposta está certa. Respostas certas e respostas da professora são reaproveitadas para perguntas iguais.</p>
        <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-200">
          <span><b className="text-white">{t.perguntas ?? 0}</b> perguntas</span><span><b className="text-white">{t.bom ?? 0}</b> aprovadas</span>
          <span><b className="text-white">{t.ruim ?? 0}</b> para corrigir</span><span>gasto: <b className="text-white">US$ {Number(t.custo_usd || 0).toFixed(2)}</b></span>
        </div>
      </div>
      <ConfigIA />
      <ConsumoIA />
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
        {conversa && (
          <div className="flex items-center gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            <MessageSquarePlus className="h-4 w-4 text-[#1E9E2A]" /> Continuando a mesma conversa: a IA lembra das perguntas anteriores.
            <button className="ml-auto inline-flex items-center gap-1 font-semibold text-slate-500 hover:text-slate-800" onClick={() => setConversa("")}><X className="h-3.5 w-3.5" /> Nova conversa</button>
          </div>
        )}
        <Textarea value={q} onChange={(e) => setQ(e.target.value)} rows={3} placeholder={conversa ? "Continue a conversa (ex.: e se o cabelo for cacheado?)" : "Digite a dúvida como uma aluna escreveria…"} onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) perguntar(); }} data-testid="ia-pergunta" />
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => perguntar()} disabled={busy || q.trim().length < 3} className="text-white" style={{ background: "#1E9E2A" }}>
            {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Pensando… (até 30 s)</> : <><Send className="h-4 w-4 mr-1.5" /> Perguntar</>}
          </Button>
          <span className="text-xs text-slate-400">Exemplos (nova conversa):</span>
          {EXEMPLOS.map((e) => <button key={e} disabled={busy} onClick={() => perguntar(e)} className="text-xs rounded-full border border-slate-200 px-2.5 py-1 text-slate-600 hover:bg-slate-50">{e}</button>)}
        </div>
      </div>
      {hist !== null && conversas.length > 0 && (
        <div className="flex flex-wrap items-end justify-between gap-3 pt-2">
          <h2 className="font-display text-lg font-bold text-slate-800">Conversas</h2>
          <Paginacao {...pag} rotulo="conversas" subir={false} />
        </div>
      )}
      {hist === null ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : pag.itens.map(([k, turnos]) => {
        const aberta = abertas.has(k) || k === conversa;
        const ult = turnos[turnos.length - 1];
        const certas = turnos.filter((r) => r.nota === "bom").length, corrigir = turnos.filter((r) => r.nota === "ruim").length;
        return (
          <div key={k} className={`bg-white rounded-xl border shadow-sm ${k === conversa ? "border-[#1E9E2A] ring-2 ring-[#1E9E2A]/15" : "border-slate-200"}`}>
            <button type="button" onClick={() => alternar(k)} className="w-full flex items-center gap-3 px-5 py-4 text-left" data-testid="ia-conversa">
              <span className="min-w-0 flex-1">
                <span className="block font-semibold text-slate-800 truncate">{turnos[0].pergunta}</span>
                <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                  <span>{turnos[0].por || "—"}</span>
                  <span>{turnos.length} pergunta(s)</span>
                  <span>{ult.at ? new Date(ult.at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                  {certas > 0 && <span className="rounded-full bg-green-50 px-2 py-0.5 font-semibold text-green-700">{certas} certa(s)</span>}
                  {corrigir > 0 && <span className="rounded-full bg-red-50 px-2 py-0.5 font-semibold text-red-700">{corrigir} para corrigir</span>}
                </span>
              </span>
              {aberta ? <ChevronUp className="h-5 w-5 text-slate-400 shrink-0" /> : <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" />}
            </button>
            {aberta && (
              <div className="border-t border-slate-100 p-5 space-y-5">
                {turnos.map((r) => <Turno key={r.id} r={r} onMudou={carregar} />)}
                {k !== conversa && <button className="text-xs font-semibold text-[#1E9E2A]" onClick={() => { setConversa(k); window.scrollTo({ top: 0, behavior: "smooth" }); }}>↳ Continuar esta conversa</button>}
              </div>
            )}
          </div>
        );
      })}
      {hist !== null && <Paginacao {...pag} rotulo="conversas" />}
    </div>
  );
}
